import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { logoUrlFromBranding } from '@/lib/clinicBranding';
import type { Json } from '@/types/database.generated';

export interface PortalClinicItem {
  patient_id: string;
  clinic_id: string;
  clinic_name: string;
  clinic_logo: string | null;
}

export const portalClinicsQueryKey = (userId: string | null) =>
  ['portal', 'clinics', userId] as const;

/**
 * Lists all clinics where the authenticated portal user has access.
 * Fetches patients first, then clinic names in a separate query so a blocked
 * clinics embed cannot zero out the whole portal context.
 */
export function usePortalClinics() {
  const { user } = useAuth();

  return useQuery({
    queryKey: portalClinicsQueryKey(user?.id ?? null),
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<PortalClinicItem[]> => {
      if (!user?.id) return [];

      const { data: patients, error: patientsError } = await supabase
        .from('patients')
        .select('id, clinic_id')
        .eq('portal_user_id', user.id)
        .eq('status', 'active')
        .order('clinic_id');

      if (patientsError) throw patientsError;
      if (!patients?.length) return [];

      const clinicIds = [...new Set(patients.map((p) => p.clinic_id))];
      const clinicById = new Map<string, { name: string; logo: string | null }>();

      if (clinicIds.length > 0) {
        const { data: clinicRows, error: clinicsError } = await supabase
          .from('clinics')
          .select('id, name, branding')
          .in('id', clinicIds);

        if (clinicsError) throw clinicsError;

        for (const row of clinicRows ?? []) {
          clinicById.set(row.id, {
            name: row.name,
            logo: logoUrlFromBranding(row.branding as Json),
          });
        }
      }

      return patients.map((row) => {
        const clinic = clinicById.get(row.clinic_id);
        return {
          patient_id: row.id,
          clinic_id: row.clinic_id,
          clinic_name: clinic?.name ?? '—',
          clinic_logo: clinic?.logo ?? null,
        };
      });
    },
    staleTime: 60_000,
  });
}
